import { PencilIcon } from '@heroicons/react/24/outline';
import React from 'react';

import { DiscriminatedCoverageUpdatedEvent } from '@/services/api/generated/matterChangesService';

import {
  ActivityActions,
  ActivityBody,
  ActivityIcon,
  ActivityItem,
  ActivityLink,
  ActivityTitle,
} from '../activity-feed-v2';
import { getUserFromEvent } from '../activity-feed-v2/ActivityDiffList';
import { CoverageFieldsChanged } from '../activity-feed-v2/CoverageFieldsChanged';

export interface CoverageUpdatedEventV2Props {
  event: DiscriminatedCoverageUpdatedEvent;
}

/**
 * V2 renderer for Coverage Updated events that uses the `previous` field
 * to show only what changed, instead of parsing the diff format.
 *
 * Falls back to showing current coverage if previous is not available.
 */
export const CoverageUpdatedEventV2: React.FC<CoverageUpdatedEventV2Props> = ({
  event,
}) => {
  const { coverage, previous } = event.payload;
  const user = getUserFromEvent(event);

  const icon = (
    <ActivityIcon
      icon={
        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-cyan-100">
          <PencilIcon className="h-4 w-4 text-cyan-600" />
        </div>
      }
    />
  );

  const carrierName = coverage?.carrier?.name;

  const body = (
    <ActivityBody>
      <div className="space-y-1">
        {carrierName && <ActivityTitle>{carrierName}</ActivityTitle>}
        <CoverageFieldsChanged previous={previous} current={coverage} />
      </div>
    </ActivityBody>
  );

  const actions = event.payload.target_id && (
    <ActivityActions>
      <ActivityLink
        href={`/matters/${event.matter_id}/coverage/${event.payload.target_id}`}
      >
        View Coverage
      </ActivityLink>
    </ActivityActions>
  );

  return (
    <ActivityItem
      icon={icon}
      title="Coverage Updated"
      timestamp={new Date(event.occurred_at ?? new Date())}
      user={user}
      body={body}
      actions={actions}
    />
  );
};
